import { memo, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { TriggerButton } from "widgets";

import { isPlaybookOnlyExecutableInGenomeViewerContext } from "@/app-utils/playbook.ts";

import type { Configuration } from "../../graphql.js";
import { isPlaybookLicensed } from "../../redux/modules/config.js";
import type { IEventState } from "../../redux/modules/event.js";
import { rerunJob } from "../../redux/modules/job.js";
import { updateNfPlaybookExecutionState } from "../../redux/modules/nf-playbook-execution.js";
import { openPlaybook, updatePlaybookExecutionState } from "../../redux/modules/playbook-execution.js";
import type { IAppState } from "../../redux/reducer.js";
import type { ReduxDispatch } from "../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../settings/settings.js";

/**
 * The Re-run button to allow re-execution of Playbooks
 */
function ReRunPlaybookButtonHeader() {
  const dispatch = useDispatch<ReduxDispatch>();
  const { event } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);

  /**
   * Drives the disabled state of the ReRun button.
   * The buttons needs to be disabled if the Playbook was
   * modified after the Playbook event was created.
   */
  function getReRunButtonDisabledTooltip(): string | undefined {
    if (!isPlaybookLicensed(configuration, event.playbook)) {
      return `You can't re-run this Playbook because of license restrictions.`;
    }

    if (isPlaybookOnlyExecutableInGenomeViewerContext(event.playbook)) {
      return `This Playbook cannot be re-run.`;
    }

    if (event.playbook.protectedPlaybook) {
      return `The access to this Playbook is restricted.`;
    }

    // Enable button if Playbook wasn't modified since its last run and it's
    // workflow approval date is also unset (in case of ngs workflows)
    if (!event.playbook.modified && !event.playbook.workflowApprovalDate) {
      return undefined;
    }

    const playbookModifiedDate = new Date(event.playbook.modified);
    const playbookEventCreatedDate = new Date(event.created);
    const playbookApprovalDate = new Date(event.playbook.workflowApprovalDate);
    const modifiedAfterCreated = playbookModifiedDate >= playbookEventCreatedDate;
    const approvedAfterCreated = playbookApprovalDate >= playbookEventCreatedDate;

    if (modifiedAfterCreated || approvedAfterCreated) {
      return `Executing the Playbook with the same parameters is not possible because the ${
        modifiedAfterCreated ? "Playbook" : "underlying Approved Workflow"
      } has been modified.`;
    }
    return undefined;
  }

  const reRunPlaybook = useCallback(() => {
    if (event.playbook.type.id === PLAYBOOK_TYPE.BLAST) {
      dispatch(rerunJob(event.job.id));
    } else {
      if (
        event.playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS ||
        event.playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES ||
        event.playbook.type.id === PLAYBOOK_TYPE.PROCESSOR
      ) {
        dispatch(updateNfPlaybookExecutionState({ initialPlaybookEventId: event.id }));
      } else {
        dispatch(updatePlaybookExecutionState({ initialPlaybookEventId: event.id }));
      }
      dispatch(openPlaybook(event.playbook));
    }
  }, [event]);

  const buttonDisabledTooltip: string = getReRunButtonDisabledTooltip();
  return (
    <TriggerButton
      id={"rerun-playbook-button"}
      icon={"redo"}
      onPress={reRunPlaybook}
      tooltip={`Click to re-run Playbook ${event.playbook.name}`}
      isDisabled={buttonDisabledTooltip}
      label={"Re-Run"}
      showLabel
    />
  );
}

export default memo(ReRunPlaybookButtonHeader);
